/**
 * EVENTS TRANSPORT
 * ================
 * Sends the analytics envelope to the events ingestion endpoint.
 *
 * Deliberately does NOT go through api/client.ts: the events API is a
 * different origin, so we don't want to auto-attach askk's session cookies/
 * headers to it. Identity travels inside the envelope body instead (built in
 * lib/analytics.ts), matching vibely-web's approach.
 *
 * This is fire-and-forget: failures are logged, never thrown, so analytics can
 * never break a user flow.
 */
import { CLIENT_TYPE, EVENTS_ENDPOINT, TENANT } from '../lib/eventConstants'
import { getIdentityHeaders } from '../lib/cookies'

const isDev = process.env.NODE_ENV !== 'production'

/** Cap a hung events POST so it can't leak a pending request indefinitely. */
const EVENTS_TIMEOUT_MS = 10000

export interface SendOptions {
  /**
   * Let the request outlive the page (fetch `keepalive`). Only the sends that
   * fire as the WebView is torn down need this (CTA redirect + page close); it
   * counts against the browser's shared 64KB keepalive budget, so leave it off
   * for sends on a live page (page-open, impressions, pseudo-clicks).
   */
  keepalive?: boolean
}

export async function sendEvents(payload: unknown, opts: SendOptions = {}): Promise<void> {
  // Dry mode: no endpoint configured. Log (dev only — the payload carries
  // identity, so never write it to the console in a prod build).
  if (!EVENTS_ENDPOINT) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.log('[analytics] dry-run (no EVENTS_ENDPOINT) — payload:', payload)
    }
    return
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), EVENTS_TIMEOUT_MS)
  try {
    const response = await fetch(EVENTS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Static tenant/client context (per the vibely-android handoff doc).
        'X-TENANT': TENANT,
        'CLIENT-TYPE': CLIENT_TYPE,
        // Auth scheme: V2 / accessToken. The backend uses this to pick the V2
        // validation path; without it the (valid) token reads as expired.
        'AUTH-VERSION': 'V2',
        // Identity headers (re-sent from the cookies the native app dropped).
        ...getIdentityHeaders(),
      },
      body: JSON.stringify(payload),
      keepalive: opts.keepalive ?? false,
      signal: controller.signal,
    })
    if (!response.ok) throw new Error(response.statusText || `HTTP ${response.status}`)
  } catch (error) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.error('[analytics] sendEvents failed:', error)
    }
  } finally {
    clearTimeout(timeoutId)
  }
}
